/**
 * OrgStructureTab — konfiguracja struktury organizacyjnej w Ustawieniach, PER FIRMA
 * (F24 Pakiet 0).
 *
 * Firma wybierana lokalnie (picker) — domyślnie firma główna. Treść ekranu = drzewo
 * jednostek organizacyjnych (`OrgUnitsTab`, `CompanyDetailDrawer.orgUnits.tsx`, ten sam
 * komponent co zakładka „Struktura" w szczegółach firmy) — dodawanie/edycja/przenoszenie/
 * reorder/soft-delete jednostek, z kierownikiem (rekord kadrowy) i rodzajem (dział/pion/
 * oddział).
 *
 * PRZED F24 Pakiet 0 ten ekran łączył DWA sposoby edycji: edytowalny organigram
 * (przeciągnij pracownika na innego = zmień przełożonego, `OrgChartCanvas editable`)
 * + drzewo działów. Organigram-edytor USUNIĘTY tutaj — `managerEmployeeId` pracownika
 * jest teraz NADPISANIEM wyjątkowym (domyślnie przełożony wynika z kierownika jednostki
 * organizacyjnej, wyliczany po stronie serwera), więc klienckie `isAncestor()` liczone
 * na płaskiej liście `managerEmployeeId` nie odzwierciedlałoby już rzeczywistej hierarchii
 * — dawałoby fałszywe/mylące blokady cykli. Podgląd struktury (read-only, per jednostka +
 * pracownicy) żyje teraz w organigramie (`/org-chart`, `OrgChart.tsx`/`OrgChartCanvas.tsx`,
 * rebase na `/org-units/tree`); zmiana przełożonego — na karcie pracownika (pole
 * „Przełożony (nadpisanie)").
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OrgUnitsTab } from '@/components/CompanyDetailDrawer.orgUnits';
import { Dropdown } from '@/components/Dropdown';
import { useCompanies } from '@/lib/use-permissions';

export function OrgStructureTab() {
  const { t } = useTranslation();

  const companiesQuery = useCompanies();
  const companies = useMemo(
    () => [...(companiesQuery.data?.companies ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [companiesQuery.data],
  );

  const [picked, setPicked] = useState<string | null>(null);
  const companyId = picked ?? companies.find((c) => c.isPrimary)?.id ?? companies[0]?.id ?? null;

  return (
    <div className="space-y-6" data-testid="settings-org-structure">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">
            {t('tenant.settings.orgStructure.title')}
          </h2>
          <p className="mt-1 text-sm text-text-tertiary">
            {t('tenant.settings.orgStructure.subtitle')}
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-text-secondary">
          {t('tenant.settings.orgStructure.company')}
          <Dropdown
            value={companyId ?? ''}
            onChange={setPicked}
            options={companies.map((c) => ({ value: c.id, label: c.shortName ?? c.legalName }))}
            className="w-56"
            aria-label={t('tenant.settings.orgStructure.company')}
            data-testid="org-structure-company-select"
          />
        </label>
      </header>

      {companyId && <OrgUnitsTab companyId={companyId} />}
    </div>
  );
}
